import React, { useState, useRef } from 'react';
import dynamic from 'next/dynamic';
import cn from 'classnames';

const ReactECharts = dynamic(() => import('echarts-for-react'), {
  ssr: false,
});
import {
  CommunityDashboardAttributes,
  CommunityTypes,
} from 'lib/models/community';

import styles from './CommunityStatusGraph.module.scss';

interface DashboardGraphProps {
  dashboardData?: CommunityDashboardAttributes;
  viewStatDetail?: (value: string) => void;
}

const CommunityStatusGraph: React.FC<DashboardGraphProps> = ({
  dashboardData,
  viewStatDetail,
}) => {
  const getGraphData = () => {
    return [
      {
        value: CommunityTypes.EVENT_VIEWS,
        headerTitle: 'Event views',
        count: dashboardData?.event_views || 0,
      },
      {
        value: CommunityTypes.PROFILE_VIEWS,
        headerTitle: 'Profile views',
        count: dashboardData?.profile_views || 0,
      },
      {
        value: CommunityTypes.ATTENDEES,
        headerTitle: 'Attendees',
        count: dashboardData?.attendees_count || 0,
      },
      {
        value: CommunityTypes.SUBSCRIBED,
        headerTitle: 'Members',
        count: dashboardData?.subscribers_count || 0,
      },
    ];
  };

  const options: Record<string, unknown> = {
    grid: {
      top: 0,
      bottom: 0,
      left: 4,
      right: 4,
      height: 148,
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: ['', '', '', '', ''],
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      splitLine: {
        show: false,
      },
    },
    yAxis: {
      show: false,
    },
    series: [
      {
        data: [150, 80, 92, 58, 53],
        type: 'line',
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              {
                offset: 0,
                color: 'rgba(0, 148, 145, 0.1)',
              },
              {
                offset: 1,
                color: 'rgba(0, 148, 145, 0.1)',
              },
            ],
          },
        },
        lineStyle: {
          color: '#009491',
          width: 1,
        },
        itemStyle: {
          opacity: 0,
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowColor: 'rgba(0, 148, 145, 0.12)',
            shadowOffsetY: -1,
            borderColor: 'white',
            borderWidth: 1,
          },
        },
      },
    ],
    markLine: {
      symbol: 'none',
      data: [
        { yAxis: 0 }, // Added to draw a line at y-axis value 0
      ],
    },
  };

  const [hoveredSection, setHoveredSection] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleChartAction = (
    e: React.MouseEvent<HTMLDivElement>,
    action: 'hover' | 'click'
  ) => {
    if (!containerRef.current) return;

    const containerWidth = containerRef.current.offsetWidth;
    const position =
      e.clientX - containerRef.current.getBoundingClientRect().left;
    const sectionWidth = containerWidth / graphData.length;

    const sectionIndex = Math.floor(position / sectionWidth);

    if (action === 'hover') {
      setHoveredSection(sectionIndex);
    } else if (action === 'click') {
      const clickedValue = graphData[sectionIndex]?.value;
      if (clickedValue && viewStatDetail) {
        viewStatDetail(clickedValue);
      }
    }
  };

  const graphData = getGraphData();
  return (
    <div className={styles.communityStatusSectionGraphBody}>
      <div
        ref={containerRef}
        className={styles.communityStatusSectionGraphLineContainer}
      >
        {graphData.map(({ headerTitle, count, value }, index) => (
          <div
            key={index}
            className={cn(styles.communityStatusSectionGraphLineContainerLine, {
              [styles.hoveredCard]: hoveredSection === index,
            })}
            onClick={() => {
              if (viewStatDetail && value) {
                viewStatDetail(value);
              }
            }}
          >
            <div
              className={cn(
                styles.communityStatusSectionGraphLineContainerLineHeader,
                {
                  [styles.first]: index === 0,
                }
              )}
            >
              <div
                className={
                  styles.communityStatusSectionGraphLineContainerLineHeaderStatus
                }
              >
                <p
                  className={
                    styles.communityStatusSectionGraphLineContainerLineHeaderTitle
                  }
                >
                  {headerTitle}
                </p>
              </div>
              <p
                className={
                  styles.communityStatusSectionGraphLineContainerLineHeaderCount
                }
              >
                {count}
              </p>
            </div>
          </div>
        ))}
      </div>
      <div
        onClick={(e) => handleChartAction(e, 'click')}
        className={styles.communityStatusSectionGraphChart}
        onMouseMove={(e) => handleChartAction(e, 'hover')}
        onMouseLeave={() => setHoveredSection(null)}
      >
        <ReactECharts
          option={options}
          opts={{ renderer: 'svg' }}
          style={{ height: '112px' }}
          className={styles.communityStatusSectionGraphEchartContainer}
        />
      </div>
    </div>
  );
};

export default CommunityStatusGraph;
